import React, { useState, useEffect } from "react";
import {
  TrendingUp,
  DollarSign,
  Users,
  MousePointer,
  Target,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import axios from "../config/axios";
import toast from "react-hot-toast";
import LoadingSpinner from "../components/LoadingSpinner";
import EmptyState from "../components/EmptyState";

/**
 * Dashboard Page Component
 * Shows overview of all marketing and sales metrics with visualizations
 */
const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("30d");

  /**
   * Fetch dashboard data from backend
   */
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/dashboard?period=${dateRange}`);
      setDashboardData(response.data);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [dateRange]);

  if (loading && !dashboardData) {
    return <LoadingSpinner message="Loading dashboard..." />;
  }

  // Check if user has any data
  const hasData =
    dashboardData &&
    ((dashboardData.metrics &&
      (dashboardData.metrics.totalRevenue > 0 ||
        dashboardData.metrics.totalSpend > 0 ||
        dashboardData.metrics.totalLeads > 0)) ||
      (dashboardData.timeSeriesData &&
        dashboardData.timeSeriesData.length > 0) ||
      (dashboardData.channelPerformance &&
        dashboardData.channelPerformance.length > 0));

  // Handle empty state
  const handleGetStarted = () => {
    // Navigate to data connections page
    window.location.href = "/data-connections";
  };

  if (!hasData && !loading) {
    return <EmptyState onGetStarted={handleGetStarted} />;
  }

  const data = dashboardData;

  // Colors for charts
  const colors = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">
            Dashboard
          </h1>
          <p className="text-sm md:text-base text-gray-600">
            Overview of your marketing and sales performance
          </p>
        </div>

        <div className="flex items-center space-x-2 md:space-x-4">
          {/* Date Range Selector */}
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="input-field w-32 text-sm touch-manipulation min-h-[44px]"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>

          {/* Refresh Button */}
          <button
            onClick={fetchDashboardData}
            disabled={loading}
            className="btn-secondary flex items-center space-x-2 text-sm touch-manipulation min-h-[44px]"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <MetricCard
          title="Total Revenue"
          value={`$${(data.metrics?.totalRevenue || 0).toLocaleString()}`}
          change={data.metrics?.revenueChange || 0}
          icon={DollarSign}
          color="green"
        />
        <MetricCard
          title="Ad Spend"
          value={`$${(data.metrics?.totalSpend || 0).toLocaleString()}`}
          change={data.metrics?.spendChange || 0}
          icon={TrendingUp}
          color="blue"
        />
        <MetricCard
          title="Total Leads"
          value={(data.metrics?.totalLeads || 0).toLocaleString()}
          change={data.metrics?.leadsChange || 0}
          icon={Users}
          color="purple"
        />
        <MetricCard
          title="Avg Cost Per Lead"
          value={`$${data.metrics?.avgCPA || 0}`}
          change={data.metrics?.cpaChange || 0}
          icon={MousePointer}
          color="yellow"
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Revenue vs Spend Chart */}
        <div className="card p-4 md:p-6">
          <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-4">
            Revenue vs Spend Trend
          </h3>
          <ResponsiveContainer
            width="100%"
            height={250}
            className="md:!h-[300px]"
          >
            <LineChart data={data.timeSeriesData || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="#10b981"
                strokeWidth={2}
                name="Revenue"
              />
              <Line
                type="monotone"
                dataKey="spend"
                stroke="#ef4444"
                strokeWidth={2}
                name="Spend"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Channel Performance */}
        <div className="card p-4 md:p-6">
          <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-4">
            Channel Performance
          </h3>
          <ResponsiveContainer
            width="100%"
            height={250}
            className="md:!h-[300px]"
          >
            <BarChart data={data.channelPerformance || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="channel" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="revenue" fill="#3b82f6" name="Revenue" />
              <Bar dataKey="spend" fill="#ef4444" name="Spend" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Goal Progress */}
      <div className="card p-4 md:p-6">
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <h3 className="text-base md:text-lg font-semibold text-gray-900">
            Goal Progress
          </h3>
          <button className="text-primary-600 hover:text-primary-700 text-xs md:text-sm font-medium touch-manipulation">
            View all →
          </button>
        </div>

        <div className="space-y-4">
          {(data.goalProgress || []).map((goal, index) => (
            <div key={index}>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-900">
                  {goal.goal}
                </span>
                <span className="text-sm text-gray-500">
                  {typeof goal.current === "number" && goal.current < 100
                    ? goal.current.toLocaleString()
                    : `$${goal.current.toLocaleString()}`}{" "}
                  /{" "}
                  {typeof goal.target === "number" && goal.target < 100
                    ? goal.target.toLocaleString()
                    : `$${goal.target.toLocaleString()}`}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(goal.progress, 100)}%` }}
                ></div>
              </div>
              <div className="mt-1 text-xs text-gray-500">
                {goal.progress}% complete
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity / Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <div className="card p-4 md:p-6">
          <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-4">
            Recent Activity
          </h3>
          <div className="space-y-3">
            <ActivityItem
              message="Google Ads data synced successfully"
              time="2 minutes ago"
              type="success"
            />
            <ActivityItem
              message="New leads from Meta Ads campaign"
              time="15 minutes ago"
              type="info"
            />
            <ActivityItem
              message="Budget alert: LinkedIn Ads nearing limit"
              time="1 hour ago"
              type="warning"
            />
          </div>
        </div>

        <div className="card p-4 md:p-6">
          <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-4">
            AI Recommendations
          </h3>
          <div className="space-y-3">
            <RecommendationItem
              title="Increase Google Ads Budget"
              description="Your best-performing campaign could benefit from 20% more budget"
              confidence={85}
            />
            <RecommendationItem
              title="Optimize Meta Ads Targeting"
              description="Consider narrowing audience to improve conversion rate"
              confidence={72}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Metric Card Component
 */
const MetricCard = ({ title, value, change, icon: Icon, color }) => {
  const isPositive = change > 0;
  const colorClasses = {
    green: "bg-green-50 text-green-600",
    blue: "bg-blue-50 text-blue-600",
    purple: "bg-purple-50 text-purple-600",
    yellow: "bg-yellow-50 text-yellow-600",
  };

  return (
    <div className="card p-4 md:p-6">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs md:text-sm font-medium text-gray-600">
            {title}
          </p>
          <p className="text-xl md:text-2xl font-bold text-gray-900 mt-1 truncate">
            {value}
          </p>
          <div className="flex items-center mt-2">
            <TrendingUp
              className={`w-3 h-3 md:w-4 md:h-4 ${isPositive ? "text-green-500" : "text-red-500 rotate-180"}`}
            />
            <span
              className={`text-xs md:text-sm font-medium ml-1 ${isPositive ? "text-green-600" : "text-red-600"}`}
            >
              {Math.abs(change)}%
            </span>
            <span className="text-xs md:text-sm text-gray-500 ml-1 hidden sm:inline">
              vs last period
            </span>
          </div>
        </div>
        <div
          className={`w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center flex-shrink-0 ml-2 ${colorClasses[color]}`}
        >
          <Icon className="w-5 h-5 md:w-6 md:h-6" />
        </div>
      </div>
    </div>
  );
};

/**
 * Activity Item Component
 */
const ActivityItem = ({ message, time, type }) => {
  const typeClasses = {
    success: "text-green-600 bg-green-50",
    info: "text-blue-600 bg-blue-50",
    warning: "text-yellow-600 bg-yellow-50",
    error: "text-red-600 bg-red-50",
  };

  return (
    <div className="flex items-start space-x-3">
      <div
        className={`w-2 h-2 rounded-full mt-2 ${typeClasses[type]?.split(" ")[1] || "bg-gray-300"}`}
      ></div>
      <div className="flex-1">
        <p className="text-sm text-gray-900">{message}</p>
        <p className="text-xs text-gray-500">{time}</p>
      </div>
    </div>
  );
};

/**
 * Recommendation Item Component
 */
const RecommendationItem = ({ title, description, confidence }) => {
  return (
    <div className="border border-primary-100 rounded-lg p-4 bg-primary-50">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium text-primary-900">{title}</h4>
        <span className="text-xs text-primary-600">
          {confidence}% confidence
        </span>
      </div>
      <p className="text-sm text-primary-800">{description}</p>
      <button className="mt-2 text-xs text-primary-600 hover:text-primary-700 font-medium">
        Apply suggestion →
      </button>
    </div>
  );
};

export default Dashboard;
